import { salesforceTarget, type FlagKind } from '../schemas/flag-type';

/**
 * What `OverrideDialog`'s confirm step says (NOXSCRUM-1670).
 *
 * It names the org and the kind of flag. For a `salesforce` flag the value
 * leaves the platform: it is projected into that customer's Salesforce org,
 * one-way, with the audit row here as the only record of why (ADR 0009 §6). For
 * a `veevart2` flag it changes what Veevart 2 services resolve for that museum
 * and nothing more. An operator should not have to remember which one this key
 * is at the moment they agree to it.
 *
 * Pure, like `platform-default-confirm-copy.ts`, so it is testable without a DOM.
 */

export interface OverrideChange {
    readonly tenantId: string;
    readonly enabled: boolean;
    /** Only ever set on a `veevart2` flag — the API refuses one on `salesforce`. */
    readonly rolloutPercentage?: number | undefined;
}

export function overrideConfirmCopy(
    flagKey: string,
    kind: FlagKind,
    change: OverrideChange,
): { readonly title: string; readonly description: string } {
    const value = change.enabled ? 'on' : 'off';

    if (kind.type === 'salesforce') {
        return {
            title: `Project ${flagKey} ${value} to org ${change.tenantId}?`,
            description:
                `${flagKey} is a Salesforce flag. Saving this override projects ${value} ` +
                `into the Salesforce org ${change.tenantId}, as ${salesforceTarget(kind)}, ` +
                `in place of the platform default. No other org changes.`,
        };
    }

    const gate =
        change.rolloutPercentage !== undefined
            ? `, gated at ${String(change.rolloutPercentage)}%`
            : '';

    return {
        title: `Override ${flagKey} for ${change.tenantId}?`,
        description:
            `${flagKey} is a Veevart 2 flag. Veevart 2 services will resolve it ${value}${gate} ` +
            `for ${change.tenantId} in place of the platform default. Nothing is sent to Salesforce.`,
    };
}
